import React from "react";
import styled from "styled-components";
import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import Status from "src/pages/InvestmentDetails/components/Details/components/Status";
import { colors } from "src/theme/colors";
import { space, text } from "src/theme/ui";

/**
 * The head of a detail page.
 *
 * Shared, because the investment page and the smartfolio page both open on
 * one subject with a way back and a couple of things to do with it — and
 * they had two headers: this one, and a bare arrow glyph in front of a
 * title with the buttons floated opposite.
 *
 * The holding's name used to be a CARD, sitting in the grid beside the
 * figures and competing with them — a page whose subject is one position
 * announced that subject in the same box as its NAV. And the four things a
 * reader can DO with the position were a fifth card of stacked full-width
 * buttons, halfway down the page, below the numbers.
 *
 * A page's subject belongs at its top, and its actions belong beside its
 * subject. The name leads, the status sits with it, the facts that identify
 * the holding run underneath as one quiet line, and the actions sit at the
 * far end of the same row where a reader looks for them.
 */

const Wrap = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space.md}px;
  /* No rule under it. The page is a column of cards on a tinted ground, so
     the head is already separated from the first row by the gap; a hairline
     across the full width added a divider the layout did not need and that
     nothing else on the page repeats. */
  padding: 0;
  margin-bottom: ${space.lg}px;
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Back = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin-bottom: 2px;
  padding: 0;
  border: 0;
  background: none;
  color: ${colors.gray[600]};
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.gray[900]};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${space.sm}px;
`;

const Name = styled.h1`
  margin: 0;
  ${text.hero}
  font-size: 22px;
  color: ${colors.gray[900]};
  overflow-wrap: anywhere;
`;

/* What the thing is. One sentence, quiet, under its name. */
const Lead = styled.p`
  margin: 2px 0 0;
  max-width: 78ch;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.gray[600]};
`;

/** The facts that identify the holding, as one line rather than five rows. */
const Meta = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${colors.gray[600]};

  span + span::before {
    content: "·";
    margin: 0 7px;
    color: ${colors.gray[300]};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${space.sm}px;
`;

type Props = {
  name?: string;
  status?: string;
  /** The line under the name: what this thing IS, in a sentence. */
  lead?: React.ReactNode;
  meta?: React.ReactNode[];
  actions?: React.ReactNode;
  /** What going back goes back TO, so a reader knows before they press it. */
  backLabel?: string;
  /**
   * Where back goes — omitted on a page that has no back.
   *
   * A top-level page reached from the sidebar is not somewhere a reader
   * arrived FROM anywhere, so it gets a title and a line about itself and no
   * arrow. Same header either way, so the two kinds of page open with the
   * same type at the same size in the same place.
   */
  onBack?: () => void;
};

const PageHeader: React.FC<Props> = ({
  name,
  status,
  lead,
  meta = [],
  actions,
  backLabel = "Back to dashboard",
  onBack,
}) => (
  <Wrap>
    <Identity>
      {onBack ? (
        <Back type="button" onClick={onBack}>
          <HugeIcon icon={ArrowLeft01Icon} size={14} />
          {backLabel}
        </Back>
      ) : null}
      <TitleRow>
        <Name>{name}</Name>
        {status ? <Status status={status} /> : null}
      </TitleRow>
      {lead ? <Lead>{lead}</Lead> : null}
      {meta.filter(Boolean).length > 0 && (
        <Meta>
          {meta.filter(Boolean).map((m, i) => (
            <span key={i}>{m}</span>
          ))}
        </Meta>
      )}
    </Identity>
    {actions ? <Actions>{actions}</Actions> : null}
  </Wrap>
);

export default PageHeader;
